import React, {useCallback, useMemo} from "react";
import {Chip, ColorSwatch, Divider, Group, GroupProps, Text} from "@mantine/core";
import {
  getUseQueryResultData,
  rangeLastNDays,
  TagDefinitionUsage,
  useGetTagDefinitionUsageQuery
} from "@ots-frontend/ots-data";
import {IconFilterCancel, IconTag} from "@tabler/icons-react";
import {createSelector} from "@reduxjs/toolkit";
import {ContainerStatusFilterFormValues} from "../form/ContainerStatusFilterForm/ContainerStatusFilterForm";

type QuickKey =
  | "neverHauled"
  | "neverDispatched"
  | "hauledLast30"
  | "dispatchedLast30";

export type ContainerStatusQuickFilterProps = Omit<GroupProps, 'spacing' | 'align' | 'noWrap'> & {
  showDispatchFilters?: boolean;
  value: ContainerStatusFilterFormValues;
  onChange: (patch: Partial<ContainerStatusFilterFormValues>) => void;
  now?: Date;
}

const swapIfNeeded = (from?: Date, to?: Date): { from?: Date; to?: Date } => {
  if (from && to && from.getTime() > to.getTime()) {
    return {from: to, to: from};
  }
  return {from, to};
};


const isSameRange = (from?: Date, to?: Date, expectedFrom?: Date, expectedTo?: Date) =>
  from?.getTime() === expectedFrom?.getTime() && to?.getTime() === expectedTo?.getTime();

const clearActivityPatch: Partial<ContainerStatusFilterFormValues> = {
  neverHauled: false,
  neverDispatched: false,
  lastHauledFromDate: undefined,
  lastHauledToDate: undefined,
  lastDispatchedFromDate: undefined,
  lastDispatchedToDate: undefined,
};

export const normalize = (
  v: ContainerStatusFilterFormValues,
  lastKey?: QuickKey
): ContainerStatusFilterFormValues => {
  const out = {...v};

  const hasHauledRange = !!out.lastHauledFromDate || !!out.lastHauledToDate;
  const hasDispatchedRange = !!out.lastDispatchedFromDate || !!out.lastDispatchedToDate;

  // Always keep ranges ordered if both ends present
  {
    const swapped = swapIfNeeded(out.lastHauledFromDate, out.lastHauledToDate);
    out.lastHauledFromDate = swapped.from;
    out.lastHauledToDate = swapped.to;
  }
  {
    const swapped = swapIfNeeded(out.lastDispatchedFromDate, out.lastDispatchedToDate);
    out.lastDispatchedFromDate = swapped.from;
    out.lastDispatchedToDate = swapped.to;
  }

  // --- DISPATCHED: either/or, precedence based on lastKey ---
  if (out.neverDispatched && hasDispatchedRange) {
    if (lastKey === "neverDispatched") {
      // never wins
      out.lastDispatchedFromDate = undefined;
      out.lastDispatchedToDate = undefined;
    } else {
      // range wins (default + when lastKey is dispatchedLast30)
      out.neverDispatched = false;
    }
  } else if (out.neverDispatched) {
    // never implies no range
    out.lastDispatchedFromDate = undefined;
    out.lastDispatchedToDate = undefined;
  } else if (hasDispatchedRange) {
    // range implies not never
    out.neverDispatched = false;
  }

  // --- HAULED: either/or, precedence based on lastKey ---
  if (out.neverHauled && hasHauledRange) {
    if (lastKey === "neverHauled") {
      out.lastHauledFromDate = undefined;
      out.lastHauledToDate = undefined;
    } else {
      out.neverHauled = false;
    }
  } else if (out.neverHauled) {
    out.lastHauledFromDate = undefined;
    out.lastHauledToDate = undefined;
  } else if (hasHauledRange) {
    out.neverHauled = false;
  }

  return out;
}

export const ContainerStatusQuickFilter = (props: ContainerStatusQuickFilterProps) => {
  const {
    showDispatchFilters = true,
    value,
    onChange,
    now,
    ...rest
  } = props

  const tagUsageSelector = useMemo(() => {
    return createSelector(
      [getUseQueryResultData<TagDefinitionUsage[]>],
      (data) => {
        return data ?? []
      }
    );
  }, [])

  const {mostUsedTagDefs} = useGetTagDefinitionUsageQuery({
    objectTypes: ["CONTAINER"],
    limit: 3
  }, {
    selectFromResult: (result) => ({
      mostUsedTagDefs: tagUsageSelector(result)
    })
  })

  const {
    from: last30From,
    to: last30To
  } = useMemo(() => {
    const _now = now ?? new Date();
    return rangeLastNDays(30, _now)
  }, [now]);

  const activeKeys = useMemo(() => {
    const keys: QuickKey[] = [];

    if (value.neverHauled) {
      keys.push("neverHauled");
    }

    if (value.neverDispatched) {
      keys.push("neverDispatched");
    }

    if (isSameRange(value.lastHauledFromDate, value.lastHauledToDate, last30From, last30To)) {
      keys.push("hauledLast30");
    }

    if (isSameRange(value.lastDispatchedFromDate, value.lastDispatchedToDate, last30From, last30To)) {
      keys.push("dispatchedLast30");
    }

    return keys;
  }, [value, last30From, last30To]);

  const activeTagDefKeys = useMemo(() => {
    const keys = new Set<string>(value.tagDefinitions?.map((def) => def.key) ?? []);
    return mostUsedTagDefs.filter((u) => keys.has(u.def.key)).map((u) => u.def.key) as string[];
  }, [value.tagDefinitions, mostUsedTagDefs]);

  const anyActivityFilterOn = activeKeys.length > 0 || activeTagDefKeys.length > 0;

  const applyPatch = useCallback((patch: Partial<ContainerStatusFilterFormValues>, key: QuickKey) => {
    // merge locally, normalize, then emit ONLY the fields we touch (activity fields)
    const merged: ContainerStatusFilterFormValues = normalize({...value, ...patch}, key);

    const activityPatch: Partial<ContainerStatusFilterFormValues> = {
      neverHauled: merged.neverHauled,
      neverDispatched: merged.neverDispatched,
      lastHauledFromDate: merged.lastHauledFromDate,
      lastHauledToDate: merged.lastHauledToDate,
      lastDispatchedFromDate: merged.lastDispatchedFromDate,
      lastDispatchedToDate: merged.lastDispatchedToDate,
    };

    onChange(activityPatch);
  }, [onChange, value]);

  const applyTagDefPatch = useCallback((tagDefKeys: string[]) => {
    const tagDefinitions = mostUsedTagDefs
      .filter((u) => tagDefKeys.includes(u.def.key))
      .map((u) => ({
        id: u.def.id,
        key: u.def.key,
        label: u.def.label,
      }));

    const merged: ContainerStatusFilterFormValues = {
      ...value,
      tagDefinitions: tagDefinitions,
    };

    onChange({
      tagDefinitions: merged.tagDefinitions,
    });
  }, [mostUsedTagDefs, onChange, value]);

  const toggleKeys = (keys: string[]) => {
    const deselectedKeys = new Set<QuickKey>(activeKeys);

    for (const key of keys) {
      if (key === "neverHauled") {
        toggleKey(key)
        deselectedKeys.delete(key);
      } else if (key === "neverDispatched") {
        toggleKey(key)
        deselectedKeys.delete(key);
      } else if (key === "hauledLast30") {
        toggleKey(key)
        deselectedKeys.delete(key);
      } else if (key === "dispatchedLast30") {
        toggleKey(key)
        deselectedKeys.delete(key);
      }
    }

    for (const key of deselectedKeys) {
      toggleKey(key)
    }
  }

  const toggleKey = (key: QuickKey) => {
    const isActive = activeKeys.includes(key);

    switch (key) {
      case "neverHauled":
        applyPatch({neverHauled: !isActive}, key);
        break;

      case "neverDispatched":
        applyPatch({neverDispatched: !isActive}, key);
        break;

      case "hauledLast30":
        applyPatch(
          isActive
            ? {lastHauledFromDate: undefined, lastHauledToDate: undefined}
            : {lastHauledFromDate: last30From, lastHauledToDate: last30To}
          , key);
        break;

      case "dispatchedLast30":
        applyPatch(
          isActive
            ? {
              lastDispatchedFromDate: undefined,
              lastDispatchedToDate: undefined,
            }
            : {
              lastDispatchedFromDate: last30From,
              lastDispatchedToDate: last30To,
            }
          , key);
        break;
    }
  };

  const toggleTagDefs = (keys: string[]) => {
    const mergedKeys = Array.from(
      new Set([
        ...(value.tagDefinitions?.map((def) => def.key) ?? []),
        ...keys,
      ])
    );

    const nextKeys = mergedKeys.filter((key) => keys.includes(key));

    applyTagDefPatch(nextKeys);
  };

  const clearFilter = useCallback(() => {
    applyPatch(clearActivityPatch, undefined)
    applyTagDefPatch([])
  }, [applyPatch, applyTagDefPatch])

  if (!showDispatchFilters && !mostUsedTagDefs.length) {
    return null;
  }

  return (
    <Group {...rest} spacing="xs" align="center" noWrap={false}>
      {showDispatchFilters &&
        <Chip.Group multiple value={activeKeys} onChange={toggleKeys}>
          <Chip size="xs" variant="filled" value="neverHauled">
            Never hauled
          </Chip>
          <Chip size="xs" variant="filled" value="neverDispatched">
            Never dispatched
          </Chip>
          <Chip size="xs" variant="filled" value="hauledLast30">
            Hauled last 30d
          </Chip>
          <Chip size="xs" variant="filled" value="dispatchedLast30">
            Dispatched last 30d
          </Chip>
        </Chip.Group>
      }
      {mostUsedTagDefs.length &&
        <>
          {showDispatchFilters && <Divider orientation="vertical" mx="xs"/>}
          <Chip.Group multiple value={activeTagDefKeys} onChange={toggleTagDefs}>
            {mostUsedTagDefs.map((u) => (
              <Chip size="xs" variant="filled" value={u.def.key}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    lineHeight: 1,
                  }}
                >
                <IconTag size={14}/>
                <span>{u.def.label}</span>
                  {u.def.colorHex && (
                    <ColorSwatch
                      h={7}
                      w={7}
                      color={u.def.colorHex}
                      style={{flexShrink: 0}}
                    />
                  )}
                </span>
              </Chip>
            ))}
          </Chip.Group>
        </>
      }
      {anyActivityFilterOn &&
        <>
          <Divider orientation="vertical" mx="xs"/>
          <Chip size="xs" variant="filled" onChange={clearFilter}>
            <IconFilterCancel size={14}/>
          </Chip>
        </>
      }
    </Group>
  );
};
